/**
 * AuthorContactPage - Contact page specifically for authors interested in 
 * uploading their books for conversion and playback on NewAudia
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { PenTool, Send, BookOpen, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthorContactPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bookTitle: '',
    genre: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send email via backend
      const response = await fetch(`${API_URL}/api/contact/author-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          book_title: formData.bookTitle,
          genre: formData.genre,
          message: formData.message,
          to_email: 'info@NewAudia.com',
          subject: `Author Inquiry: ${formData.name} - ${formData.bookTitle || 'New Submission'}`
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Your inquiry has been sent successfully!');
        
        // Return to landing page after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2500);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending author inquiry:', error);
      toast.error('Failed to send your inquiry. Please try again or call us at (719) 205-6381.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Thank You!</h2>
          <p className="text-slate-300 mb-6">
            Your inquiry has been submitted successfully. Our team will review your information and get back to you shortly.
          </p>
          <p className="text-sm text-slate-400">
            Redirecting you to the homepage...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <img src="/newaudia-logo.png" alt="NewAudia" className="h-10" />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-6">
            <PenTool className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Welcome, Author!
          </h1>
          <p className="text-lg text-slate-300 mb-2">
            We're excited about your interest in bringing your book to life on NewAudia.
          </p>
          <p className="text-slate-400">
            Fill out the form below and our team will guide you through the conversion process.
          </p>
        </div>

        {/* Benefits Banner */}
        <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-400" />
            What You'll Get
          </h3>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-300">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              AI-powered audio conversion
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Interactive reader experience
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Earn commissions on engagement
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Dedicated author support
            </li>
          </ul>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-white mb-6">Tell Us About Yourself</h2>
          
          <div className="space-y-5">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-slate-300 mb-2 block">
                Full Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-slate-300 mb-2 block">
                Email Address <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-slate-300 mb-2 block">
                Phone Number <span className="text-slate-500">(optional)</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
              />
            </div>

            {/* Book Title */}
            <div>
              <Label htmlFor="bookTitle" className="text-slate-300 mb-2 block">
                Book Title <span className="text-slate-500">(if applicable)</span>
              </Label>
              <Input
                id="bookTitle"
                name="bookTitle"
                type="text"
                value={formData.bookTitle}
                onChange={handleChange}
                placeholder="The title of your book"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
              />
            </div>

            {/* Genre */}
            <div>
              <Label htmlFor="genre" className="text-slate-300 mb-2 block">
                Genre <span className="text-slate-500">(optional)</span>
              </Label>
              <Input
                id="genre"
                name="genre"
                type="text"
                value={formData.genre}
                onChange={handleChange}
                placeholder="e.g., Fiction, Non-fiction, Self-help, etc."
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-slate-300 mb-2 block">
                Message <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your book and what you're looking to achieve with NewAudia..."
                rows={5}
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 resize-none"
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              data-testid="author-contact-submit-btn"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Send className="w-5 h-5" />
                  Submit Inquiry
                </span>
              )}
            </Button>
          </div>

          {/* Contact Alternative */}
          <p className="text-center text-sm text-slate-400 mt-6">
            Prefer to talk? Call us directly at{' '}
            <a href="tel:+17192056381" className="text-blue-400 hover:text-blue-300 font-semibold">
              (719) 205-6381
            </a>
          </p>
        </form>

        {/* Accepted Formats Note */}
        <div className="mt-8 text-center text-sm text-slate-400">
          <p>
            <span className="text-slate-300 font-medium">Accepted formats:</span> EPUB and Microsoft Word (.docx or .doc)
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthorContactPage;
